﻿import {NamedValue} from "@2d-front-monorepo/core";
import React, {JSX, useCallback, useMemo} from "react";
import {useControlId} from "../helpers/useControlId";
import OptionList, {OptionListEntry} from "../HtmlControls/OptionList";
import {MvvmContextState} from "./MvvmContextState";
import {MvvmControlProps} from "./MvvmControlProps";

import {useRegisterValidation} from "./useRegisterValidation";
import {useSelectedItem} from "./useSelectedItem";

export type MvvmOptionListEntry<TValue> = NamedValue<TValue> & {
	disabled?: boolean;
}

export type MvvmOptionListProps<T extends object = never, TValue = never> =
	Omit<MvvmControlProps<T, TValue>, "label">
	& {
	items: MvvmOptionListEntry<TValue>[];
}

export default function MvvmOptionList<T extends object, TValue>(props: MvvmOptionListProps<T, TValue>): JSX.Element {
	const {
		context,
		property,
		id,
		items,
		column,
		hidden,
		disabled,
		validation
	} = props;

	const controlId = useControlId(id);

	const itemMap = useMemo<OptionListEntry[]>(() => items.map((x, index) => ({
		value: index.toString(),
		label: x.label,
		disabled: x.disabled
	})), [items]);

	const value = context.getValue<TValue>(props.property);
	const selectedValues = [useSelectedItem(items, value)];

	const onChange = useCallback(function (s: number[]): void {
		(context as MvvmContextState<T>).setValue(property, items.at(s[0])?.value);
	}, [context, property, validation, controlId]);

	useRegisterValidation(context, property, controlId, validation);

	if (hidden) {
		return <></>;
	}

	return <OptionList name={controlId}
	                   items={itemMap}
	                   values={selectedValues}
	                   disabled={disabled}
	                   column={column}
	                   hidden={hidden}
	                   onChange={onChange}/>;
}
